<template>
  <div class="relative">
    <!-- Menu Button -->
    <button
      ref="menuButton"
      type="button"
      class="inline-flex w-fit justify-center rounded-md bg-white text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none active:translate-y-px"
      :class="$slots.menu_button ? '' : 'border border-gray-300 px-1.5 py-0.5'"
      @click="toggleMenu"
    >
      <template v-if="!$slots.menu_button">
        <DotsHorizontal class="h-4 w-4 text-gray-700" />
        <span
          v-if="indicator"
          class="absolute right-0 top-0 block h-1.5 w-1.5 rounded-full bg-[rgb(126,173,219)] ring-2 ring-white"
        />
      </template>
      <template v-else>
        <slot name="menu_button"></slot>
      </template>
    </button>

    <!-- Dropdown Menu -->
    <Teleport to="body">
      <div
        ref="menuItems"
        class="actions-menu-scrollbar absolute z-10 w-64 divide-y-4 divide-gray-100 rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 transition duration-100 focus:outline-none"
        :style="menuStyles"
        :class="{
          'pointer-events-none scale-95 opacity-0': !open,
          'pointer-events-auto scale-100 opacity-100': open,
        }"
      >
        <slot></slot>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, computed } from 'vue'
import DotsHorizontal from './icons/dots-horizontal.vue'
import { onClickOutside } from '@vueuse/core'

defineProps({
  indicator: {
    type: Boolean,
    default: false,
  },
})

const open = ref(false)
const menuButton = ref<HTMLElement | null>(null)
const menuItems = ref<HTMLElement | null>(null)
const opensUpward = ref(false)

const toggleMenu = (e: MouseEvent) => {
  menuButton.value = e.currentTarget as HTMLElement
  open.value = !open.value
  if (open.value) {
    autoPosition()
  }
}

const autoPosition = () => {
  if (!menuButton.value || !menuItems.value) return
  nextTick(() => {
    const buttonRect = menuButton.value!.getBoundingClientRect()
    const menu = menuItems.value!
    const menuHeight = menu.scrollHeight

    // Find the closest table container
    const tableContainer = menuButton.value!.closest('.vtable')
    const tableRect = tableContainer?.getBoundingClientRect()

    // Check if menu would extend past the table's bottom edge
    const tableBottom = tableRect?.bottom ?? window.innerHeight
    const menuBottom = buttonRect.bottom + menuHeight
    opensUpward.value = menuBottom > tableBottom

    const viewportPad = 8
    const available = opensUpward.value
      ? buttonRect.top - viewportPad
      : window.innerHeight - buttonRect.bottom - viewportPad

    menu.style.maxHeight = `${Math.max(available, 0)}px`
    menu.style.overflowY = 'auto'

    if (opensUpward.value) {
      const renderedHeight = Math.min(menuHeight, available)
      menu.style.top = `${buttonRect.top + window.scrollY - renderedHeight}px`
    } else {
      menu.style.top = `${buttonRect.bottom + window.scrollY}px`
    }
    menu.style.right = `${window.innerWidth - buttonRect.right + window.scrollX}px`
  })
}

onClickOutside(menuButton, (e: MouseEvent) => {
  if (!open.value) return

  const target = e.target as Node
  if (menuButton.value?.contains(target) || menuItems.value?.contains(target)) {
    return
  }

  open.value = false
})

const menuStyles = computed(() => ({
  position: 'absolute',
  transformOrigin: opensUpward.value ? 'bottom right' : 'top right',
}))

const handleScroll = (e: Event) => {
  if (menuItems.value && e.target instanceof Node && menuItems.value.contains(e.target)) return
  autoPosition()
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, true)
  window.addEventListener('resize', autoPosition)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll, true)
  window.removeEventListener('resize', autoPosition)
})
</script>

<style>
/* Unscoped because the menu is teleported to <body>; namespaced via .actions-menu-scrollbar so the rules don't leak. */
.actions-menu-scrollbar {
  scrollbar-color: #d1d5db transparent; /* Firefox: thumb / track */
}
.actions-menu-scrollbar::-webkit-scrollbar {
  width: 10px;
}
.actions-menu-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.actions-menu-scrollbar::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 8px;
}
.actions-menu-scrollbar::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}
</style>
